import { Card, CardContent, CardHeader, CardTitle } from "@/lib/shadcn/ui/card";
import { Label } from "@/lib/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import type { NeteraCredentialLabel } from "@/models/responses";

const N_A_LABEL = "N/A — doesn't file";
const SELECT_TESTID = "netera-account-select";
const ERROR_ID = "netera-account-error";

export interface NeteraAccountSelectProps {
    /** The currently-linked Netera credentials row id, or `null` for "N/A". */
    value: number | null;
    /** Called with the newly-selected id (or `null` when "N/A" is chosen). */
    onChange: (id: number | null) => void;
    /** The credential labels offered, in API response order. */
    labels: NeteraCredentialLabel[];
    /** The labels query is in-flight → disable the editable select. */
    isLoading?: boolean;
    /** The labels query failed → disable + surface an inline error. */
    isError?: boolean;
    /**
     * Read-only view mode (the details page before "Edit Details"). Renders the
     * current account as static, screen-reader-readable text INSTEAD of a
     * disabled combobox — a native-`disabled` trigger drops out of the tab order,
     * hiding the current value from assistive tech (W4 a11y fix). The add form
     * never sets this: it is always editable.
     */
    readOnly?: boolean;
}

/**
 * "Tax Invoicing (Netera)" card + account selector, shared by the add form and
 * the apartment-details edit form. Owns the `"none"` sentinel, the label
 * resolution (by row id, so renamed labels show their current text — EC-002),
 * the helper prose, and the degraded (load-error) state.
 */
export function NeteraAccountSelect({
    value,
    onChange,
    labels,
    isLoading = false,
    isError = false,
    readOnly = false,
}: NeteraAccountSelectProps) {
    const selectValue = value == null ? "none" : String(value);
    // Resolve the display label from the labels list by row id. Always a defined
    // string so `SelectValue` keeps its own children and Radix never portals the
    // selected item's text into it. While a linked apartment's labels are still
    // loading the id won't resolve → transient N/A, corrected once the list lands.
    const displayLabel =
        value == null ? N_A_LABEL : (labels.find((l) => l.id === value)?.label ?? N_A_LABEL);

    const disabled = isLoading || isError;

    return (
        <Card className="h-fit">
            <CardHeader>
                <CardTitle>Tax Invoicing (Netera)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-2">
                    <Label htmlFor={SELECT_TESTID}>Netera Invoicing Account</Label>
                    {readOnly ? (
                        // Static, focusable-in-reading-order text keeps the current
                        // account readable by AT in view mode (W4).
                        <p id={SELECT_TESTID} data-testid={SELECT_TESTID} className="text-sm">
                            {displayLabel}
                        </p>
                    ) : (
                        <Select
                            value={selectValue}
                            disabled={disabled}
                            onValueChange={(v) => onChange(v === "none" ? null : Number(v))}
                        >
                            <SelectTrigger
                                id={SELECT_TESTID}
                                data-testid={SELECT_TESTID}
                                className="w-full"
                                aria-invalid={isError ? "true" : undefined}
                                aria-describedby={isError ? ERROR_ID : undefined}
                            >
                                <SelectValue placeholder={N_A_LABEL}>{displayLabel}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">{N_A_LABEL}</SelectItem>
                                {labels.map((label) => (
                                    <SelectItem key={label.id} value={String(label.id)}>
                                        {label.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    <p className="text-sm text-gray-500">
                        Which Netera/MyData account files this apartment&apos;s tax receipts. N/A means this
                        apartment doesn&apos;t file.
                    </p>
                    {isError && (
                        <p id={ERROR_ID} className="text-sm text-red-600">
                            Couldn&apos;t load accounts — retry.
                        </p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
